import { useRef, useState } from 'react'

import { TRACK_CONTENT_TYPES } from '@mc/track-event'
import {
  VideoEventHandlers,
  VideoExternalPlayState,
  VideoPlayback,
} from '@mc/video'
import {
  Button,
  IconVolumeOffDefault,
  IconVolumeOnDefault,
} from '@mc/design-system'

import { VideoPlayerNoTracking } from 'components/VideoPlayer'

import { useVisibilityChanged } from './visibilityHooks'
import {
  trackVideoError,
  trackVideoMuteButtonClicked,
  trackVideoPlayButtonClicked,
  trackVideoStateChanged,
  trackVisibilityChanged,
} from './events'

import styles from './CMTrailerHero.module.scss'

export const HeroTrailer = ({
  videoUuid,
  show,
  isTrailerPlaying,
  setIsTrailerReady,
  setIsTrailerPlaying,
}: {
  show: boolean
  isTrailerPlaying: boolean
  setIsTrailerReady: (isTrailerReady: boolean) => void
  setIsTrailerPlaying: (isTrailerPlaying: boolean) => void
  videoUuid?: string | null
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isTrailerMuted, setIsTrailerMuted] = useState(true)

  useVisibilityChanged(ref, (isVisible: boolean) => {
    if (!isVisible && isTrailerPlaying) {
      setIsTrailerPlaying(false)
      setIsTrailerMuted(true)
    } else if (show && isVisible && !isTrailerPlaying) {
      setIsTrailerPlaying(true)
    }
    trackVisibilityChanged({ isVisible })
  })

  if (!videoUuid) return null

  return (
    <div
      className={`position-absolute mc-inset-0 ${styles.videoContainer} ${show ? styles.show : styles.hide}`}
      ref={ref}
    >
      <VideoPlayerNoTracking
        mediaUuid={videoUuid}
        contentType={TRACK_CONTENT_TYPES.COURSE_TRAILER}
        contentTitle='CM Hero Trailer'
        className='mc-h-100'
        fit='cover'
        muted={isTrailerMuted}
        style={{ objectPosition: '50% 18%' }}
        loop
        poster={undefined}
      >
        <VideoControl
          isTrailerMuted={isTrailerMuted}
          isTrailerPlaying={isTrailerPlaying}
          setIsTrailerPlaying={setIsTrailerPlaying}
          setIsTrailerMuted={setIsTrailerMuted}
          setIsTrailerReady={setIsTrailerReady}
        />
      </VideoPlayerNoTracking>
    </div>
  )
}

const VideoControl = ({
  isTrailerMuted,
  isTrailerPlaying,
  setIsTrailerReady,
  setIsTrailerMuted,
  setIsTrailerPlaying,
}: {
  isTrailerMuted: boolean
  isTrailerPlaying: boolean
  setIsTrailerReady: (isTrailerReady: boolean) => void
  setIsTrailerMuted: (isTrailerMuted: boolean) => void
  setIsTrailerPlaying: (isPlaying: boolean) => void
}) => {
  const toggleMute = () => {
    setIsTrailerMuted(!isTrailerMuted)
    trackVideoMuteButtonClicked({ didMute: !isTrailerMuted })
  }

  const togglePlay = () => {
    setIsTrailerPlaying(!isTrailerPlaying)
    trackVideoPlayButtonClicked({ didPlay: !isTrailerPlaying })
  }

  return (
    <>
      <div
        className={`position-absolute d-flex flex-column mc-gap-3 mc-p-4 ${styles.videoControls}`}
      >
        <Button kind='play' rounded onClick={toggleMute}>
          {isTrailerMuted ? <IconVolumeOffDefault /> : <IconVolumeOnDefault />}
        </Button>
        <VideoPlayback
          kind='play'
          rounded
          size='medium'
          onClick={togglePlay}
          className={styles.pauseButton}
        />
      </div>
      <VideoExternalPlayState playing={isTrailerPlaying} />
      <VideoEventHandlers
        onReady={() => setIsTrailerReady(true)}
        onError={({ error }) => {
          trackVideoError({ error: String(error) })
          setIsTrailerReady(false)
        }}
        onPlay={() => trackVideoStateChanged({ state: 'play' })}
        onPause={() => trackVideoStateChanged({ state: 'pause' })}
        onEnded={() => trackVideoStateChanged({ state: 'ended' })}
      />
    </>
  )
}
